/**
 * Réglages publics d'authentification du serveur : « Google est-il activé pour CE produit ? »
 *
 * Le serveur répond, sans identification de la personne, à `GET <API>/auth/v1/settings` :
 * `{ "external": { "google": true, … }, … }`. Le bouton « Continuer avec Google » n'est AFFICHÉ que si la réponse
 * dit `true` : un produit où Google n'est pas (encore) branché n'affiche donc jamais de bouton cassé, et l'activer
 * côté serveur suffit à le faire apparaître, sans nouveau déploiement de la console.
 *
 * Module pur : pas de `window`, pas de `import.meta`.
 */

/** Vrai seulement si le fournisseur est déclaré ACTIF. Réponse absente, illisible ou ambiguë : faux. */
export function fournisseurDansReglages(reglages: unknown, nom: 'google'): boolean {
  if (!reglages || typeof reglages !== 'object') return false
  const externe = (reglages as { external?: unknown }).external
  if (!externe || typeof externe !== 'object') return false
  return (externe as Record<string, unknown>)[nom] === true
}

export interface ConfigReglages {
  /** Adresse de l'API, sans barre finale. */
  url: string
  /** Clé publique du projet (celle que la console embarque déjà). */
  cle: string
  /** Injectable pour les tests. */
  fetchImpl?: typeof fetch
  /** Délai maximal avant d'abandonner (défaut : 6 s). */
  delaiMs?: number
}

/**
 * `true` / `false` = état déclaré du fournisseur ; `null` = impossible de le savoir (adresse ou clé absente, réseau
 * coupé, réponse non JSON). Dans le doute (`null`), l'appelant n'affiche PAS le bouton.
 */
export async function lireFournisseurActif(config: ConfigReglages, nom: 'google' = 'google'): Promise<boolean | null> {
  if (!config.url || !config.cle) return null
  const appeler = config.fetchImpl ?? fetch
  const controle = new AbortController()
  const minuteur = setTimeout(() => controle.abort(), config.delaiMs ?? 6000)
  try {
    const r = await appeler(`${config.url}/auth/v1/settings`, { headers: { apikey: config.cle }, signal: controle.signal })
    if (!r.ok) return null
    return fournisseurDansReglages(await r.json(), nom)
  } catch {
    return null
  } finally {
    clearTimeout(minuteur)
  }
}
